import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { EmptyState, PageLoading } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import type { Holiday } from '../../types';

/**
 * The company calendar — which dates are company holidays.
 *
 * Kept as a plain per-year list rather than a month grid: an admin sets a
 * dozen or so a year, and a list is faster to scan and edit than clicking
 * cells. The app and the attendance history read these to mark a day a
 * holiday rather than an absence.
 */
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fmt(dateStr: string): { day: string; weekday: string } {
  const d = new Date(`${dateStr}T12:00:00Z`);
  return {
    day: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }),
    weekday: WEEKDAY[d.getUTCDay()] ?? '',
  };
}

export default function CalendarPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ date: '', name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHolidays(await api.get<Holiday[]>('/leave/holidays', {
        from: `${year}-01-01`, to: `${year}-12-31`,
      }));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the calendar.');
    } finally {
      setLoading(false);
    }
  }, [year, toast]);

  useEffect(() => { void load(); }, [load]);

  async function add() {
    if (!form.date || !form.name.trim() || busy) return;
    setBusy(true);
    try {
      await api.post('/leave/holidays', { date: form.date, name: form.name.trim() });
      setForm({ date: '', name: '' });
      toast.success('Holiday added.');
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(h: Holiday) {
    setBusy(true);
    try {
      await api.del(`/leave/holidays/${h.id}`);
      toast.success('Holiday removed.');
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  const years = [thisYear - 1, thisYear, thisYear + 1];

  return (
    <>
      <PageHeader
        title="Company calendar"
        subtitle="Company holidays for the year. A holiday is not counted as an absence."
        actions={(
          <select className="select" style={{ maxWidth: 120 }} value={year}
            onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      />

      <div className="page col gap-16">
        {isAdmin ? (
          <div className="card">
            <div className="row gap-8" style={{ padding: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label className="field-label">Date</label>
                <input type="date" className="input" style={{ maxWidth: 180 }}
                  value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
              </div>
              <div className="grow" style={{ minWidth: 200 }}>
                <label className="field-label">Holiday name</label>
                <input className="input" placeholder="e.g. Diwali" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} />
              </div>
              <button type="button" className="btn btn-primary" disabled={busy || !form.date || !form.name.trim()}
                onClick={() => void add()}>
                <Icon name="plus" size={16} /> Add
              </button>
            </div>
          </div>
        ) : null}

        {loading ? <PageLoading /> : holidays.length === 0 ? (
          <div className="card">
            <EmptyState icon="date" title={`No holidays set for ${year}`}
              body={isAdmin ? 'Add your company holidays above.' : 'Nothing here yet.'} />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Date</th><th>Day</th><th>Holiday</th>{isAdmin ? <th /> : null}</tr>
                </thead>
                <tbody>
                  {holidays.map((h) => {
                    const f = fmt(h.date);
                    return (
                      <tr key={h.id}>
                        <td className="primary nowrap">{f.day}</td>
                        <td>{f.weekday}</td>
                        <td>{h.name}</td>
                        {isAdmin ? (
                          <td>
                            <button type="button" className="icon-btn" title="Remove"
                              disabled={busy} onClick={() => void remove(h)}>
                              <Icon name="trash" size={15} />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
